import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { login } from "../services/api";
import { Loader2, ArrowLeft, RefreshCw } from "lucide-react";

function Otp() {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState("");
  const [resendCountdown, setResendCountdown] = useState(30);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get email and password from URL params
  useEffect(() => {
    const emailParam = searchParams.get("email");
    const passwordParam = searchParams.get("password");
    
    if (!emailParam) {
      navigate("/login");
      return;
    }
    
    setEmail(emailParam);
    if (passwordParam) {
      setPassword(passwordParam);
    }
    
    // Focus first OTP input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
    
    // Set up countdown for resend OTP
    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [navigate, searchParams]);

  const handleChange = (value, index) => {
    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError("");

      // Auto-focus next input
      if (value && index < otp.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
      
      // Auto-submit when last digit is entered
      if (index === otp.length - 1 && value) {
        const fullOtp = [...newOtp].join("");
        if (fullOtp.length === 6) {
          handleSubmit({ preventDefault: () => {} }, fullOtp);
        }
      }
    }
  };
  
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };
  
  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text/plain').trim();
    if (/^\d{6}$/.test(pasteData)) {
      const otpArray = pasteData.split('').slice(0, 6);
      setOtp(otpArray);
      // Auto-submit on paste if we have 6 digits
      handleSubmit({ preventDefault: () => {} }, otpArray.join(''));
    }
  };
  
  const handleResendOtp = async () => {
    if (resendCountdown > 0 || !email) return;
    
    setIsResending(true);
    setError("");
    
    try {
      await login(email, password);
      setResendCountdown(30);
      // Restart countdown
      const timer = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e, otpValue = null) => {
    e.preventDefault();
    const enteredOtp = otpValue || otp.join("");
    
    if (enteredOtp.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      await verifyOtp(email, enteredOtp, 'email');
      // If verification is successful, navigate to dashboard
      navigate("/dashboard");
    } catch (error) {
      setError(error.response?.data?.message || "Verification failed. Please try again.");
      // Clear OTP on error
      setOtp(["", "", "", "", "", ""]);
      // Focus first input on error
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen items-center justify-center ">
      <div className="flex w-full max-w-5xl min-h-[600px] rounded-xl shadow-lg overflow-hidden">
        {/* Left gray block */}
        <div className="hidden md:flex w-2/5 bg-gray-200 relative overflow-hidden">
          <img 
            src="/loginImage.png" 
            alt="Login illustration"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Right OTP card */}
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="w-full max-w-sm rounded-xl p-6 shadow-sm bg-white dark:bg-black">
            {/* Back */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
            >
              <ArrowLeft size={16} className="mr-1" /> Back
            </button>

            {/* Heading */}
            <h2
              className="text-gray-700 text-2xl font-bold mb-2"
              style={{ fontFamily: "Montserrat" }}
            >
              Enter OTP
            </h2>

            {/* Info text */}
            <p className="text-gray-500 text-sm mb-2">
              We have sent a verification code to
            </p>
            <p className="text-gray-700 text-sm font-medium mb-6">{email}</p>

            {/* OTP form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex justify-between gap-2 mb-6">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleChange(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className={`w-12 h-14 border-2 ${error ? 'border-red-500' : 'border-gray-200'} rounded-lg text-center text-2xl focus:ring-2 focus:ring-[#FFA500] focus:outline-none focus:border-[#FFA500] transition-colors`}
                    disabled={isLoading}
                    autoFocus={index === 0}
                  />
                ))}
              </div>
              
              <div className="text-center mb-6">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCountdown > 0 || isResending}
                  className={`text-sm ${resendCountdown > 0 ? 'text-gray-400' : 'text-[#FFA500] hover:underline'}`}
                >
                  {isResending ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      Sending...
                    </span>
                  ) : resendCountdown > 0 ? (
                    `Resend OTP in ${resendCountdown}s`
                  ) : (
                    <span className="flex items-center justify-center">
                      <RefreshCw className="mr-1 h-4 w-4" />
                      Resend OTP
                    </span>
                  )}
                </button>
              </div>

              {error && (
                <div className="text-red-500 text-sm mb-4 text-center">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full bg-[#FFA500] text-white py-2 rounded-lg font-medium hover:bg-orange-400 transition flex items-center justify-center ${
                  isLoading ? 'opacity-75 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Otp;
